import { Archive, Loader2, Plus, Tag } from "lucide-react";
import type { Id } from "../../../convex/_generated/dataModel";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArchiveTagDialog, EditTagDialog } from "./tag-dialogs";
import type {
  ManagedTag,
  TagDraft,
  TagFormSubmitHandler,
} from "./settings-types";
import { formatCount, getTagColorClass } from "./settings-utils";

type TagsSectionProps = {
  tags: ManagedTag[];
  isLoading: boolean;
  totalTags: number;
  totalAssignments: number;
  createDraft: TagDraft;
  editDraft: TagDraft;
  editingTag: ManagedTag | null;
  archivingTag: ManagedTag | null;
  pendingTagAction: Id<"tag"> | "new" | null;
  createFormError: string | null;
  editFormError: string | null;
  archiveFormError: string | null;
  onCreateDraftChange: (draft: TagDraft) => void;
  onEditDraftChange: (draft: TagDraft) => void;
  onCreateSubmit: TagFormSubmitHandler;
  onEditSubmit: TagFormSubmitHandler;
  onEdit: (tag: ManagedTag) => void;
  onCancelEdit: () => void;
  onRequestArchive: (tag: ManagedTag) => void;
  onCancelArchive: () => void;
  onArchive: (tag: ManagedTag) => void;
};

export function TagsSection({
  tags,
  isLoading,
  totalTags,
  totalAssignments,
  createDraft,
  editDraft,
  editingTag,
  archivingTag,
  pendingTagAction,
  createFormError,
  editFormError,
  archiveFormError,
  onCreateDraftChange,
  onEditDraftChange,
  onCreateSubmit,
  onEditSubmit,
  onEdit,
  onCancelEdit,
  onRequestArchive,
  onCancelArchive,
  onArchive,
}: TagsSectionProps) {
  const isCreatingTag = pendingTagAction === "new";

  return (
    <>
      <div className="grid gap-3 xl:grid-cols-[1fr_17rem]">
        <Card className="rounded-lg py-0">
          <CardHeader className="border-b px-4 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Tag className="h-4 w-4" />
                  Property tags
                </CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Create custom tags that agents can apply to property records.
                </p>
              </div>
              <Badge variant="secondary">{formatCount(totalTags)} active</Badge>
            </div>
          </CardHeader>

          <CardContent className="space-y-4 px-4 py-4">
            <CreateTagForm
              draft={createDraft}
              isSaving={isCreatingTag}
              formError={createFormError}
              onDraftChange={onCreateDraftChange}
              onSubmit={onCreateSubmit}
            />

            {isLoading ? (
              <div className="flex min-h-24 items-center justify-center rounded-lg border bg-muted/20">
                <Loader2
                  className="h-5 w-5 animate-spin text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="sr-only">Checking property tags.</span>
              </div>
            ) : tags.length === 0 ? (
              <div className="rounded-lg border border-dashed bg-muted/20 p-4">
                <p className="text-sm font-medium">No property tags yet.</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Create your first tag so agents can label listings
                  consistently.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border">
                {tags.map((tag) => (
                  <TagRow
                    key={tag._id}
                    tag={tag}
                    pendingTagAction={pendingTagAction}
                    onEdit={onEdit}
                    onRequestArchive={onRequestArchive}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <aside className="space-y-3">
          <div className="rounded-lg border bg-card p-3">
            <p className="text-xs font-medium text-muted-foreground">
              Tag coverage
            </p>
            <p className="mt-2 text-2xl font-semibold tabular-nums">
              {formatCount(totalAssignments)}
            </p>
            <p className="text-xs text-muted-foreground">
              active property assignments
            </p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <p className="text-sm font-medium">How tags work</p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Tags appear in property forms and filters. Archiving hides a tag
              from active lists while preserving existing property references.
            </p>
          </div>
        </aside>
      </div>

      <EditTagDialog
        tag={editingTag}
        draft={editDraft}
        formError={editFormError}
        pendingTagAction={pendingTagAction}
        onDraftChange={onEditDraftChange}
        onSubmit={onEditSubmit}
        onCancel={onCancelEdit}
      />

      <ArchiveTagDialog
        tag={archivingTag}
        formError={archiveFormError}
        pendingTagAction={pendingTagAction}
        onCancel={onCancelArchive}
        onArchive={onArchive}
      />
    </>
  );
}

type CreateTagFormProps = {
  draft: TagDraft;
  isSaving: boolean;
  formError: string | null;
  onDraftChange: (draft: TagDraft) => void;
  onSubmit: TagFormSubmitHandler;
};

function CreateTagForm({
  draft,
  isSaving,
  formError,
  onDraftChange,
  onSubmit,
}: CreateTagFormProps) {
  return (
    <form className="rounded-lg border bg-muted/20 p-3" onSubmit={onSubmit}>
      <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <div>
          <Label htmlFor="property-tag-name">Tag name</Label>
          <Input
            id="property-tag-name"
            value={draft.tagName}
            onChange={(event) =>
              onDraftChange({
                ...draft,
                tagName: event.target.value,
              })
            }
            placeholder="Beachfront"
            disabled={isSaving}
            className="mt-2"
          />
        </div>
        <div>
          <Label htmlFor="property-tag-name-idn">Indonesian label</Label>
          <Input
            id="property-tag-name-idn"
            value={draft.tagNameIdn}
            onChange={(event) =>
              onDraftChange({
                ...draft,
                tagNameIdn: event.target.value,
              })
            }
            placeholder="Tepi pantai"
            disabled={isSaving}
            className="mt-2"
          />
        </div>
        <Button type="submit" disabled={isSaving}>
          <Plus className="h-4 w-4" />
          {isSaving ? "Saving tag" : "Create tag"}
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Duplicate names are reused instead of creating another tag.
      </p>
      {formError !== null ? (
        <p className="mt-2 text-sm text-destructive">{formError}</p>
      ) : null}
    </form>
  );
}

type TagRowProps = {
  tag: ManagedTag;
  pendingTagAction: Id<"tag"> | "new" | null;
  onEdit: (tag: ManagedTag) => void;
  onRequestArchive: (tag: ManagedTag) => void;
};

function TagRow({
  tag,
  pendingTagAction,
  onEdit,
  onRequestArchive,
}: TagRowProps) {
  const isPending = pendingTagAction === tag._id;
  return (
    <div className="grid gap-2 border-b p-3 last:border-b-0 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`border ${getTagColorClass(tag._id)}`}>
            {tag.tag_name}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatCount(tag.usageCount)} properties
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Indonesian label: {tag.tag_name_idn}
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onEdit(tag)}
          disabled={isPending}
        >
          Edit tag
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onRequestArchive(tag)}
          disabled={isPending}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Archive className="h-4 w-4" />
          Archive tag
        </Button>
      </div>
    </div>
  );
}
